import {
  canonicalize,
  decryptPayload,
  encryptPayload,
  fromB64Url,
  isHex64,
  toB64Url,
  utf8ToBytes,
  type Bundle,
  type Envelope,
} from "@psplit/ledger-core";

// App-level messages inside encrypted envelopes. Three kinds:
//  - "ops":   a ledger bundle (the normal sync payload)
//  - "hello": a join request - the invitee announces its pubkey so any existing
//             member's app can author the member_add. Possession of the group
//             key IS the invitation, so members auto-accept hellos.
//  - "req":   "here is what I already have" (my DAG heads) - the other device
//             answers with a delta bundle instead of the whole history. Used
//             only by face-to-face QR sync; the relay path has cursors.

export type WireMsg =
  | { t: "ops"; b: Bundle }
  | { t: "hello"; pub: string; name: string }
  | { t: "req"; heads: string[] };

const decoder = new TextDecoder();

// Deflate the plaintext before encrypting (ciphertext does not compress).
// Ledger JSON halves or better; hex hashes and signatures still shrink some.
const canCompress =
  typeof CompressionStream !== "undefined" && typeof DecompressionStream !== "undefined";

// Bound a stream's output: error past `max` bytes. A hostile group-key holder
// could otherwise craft a tiny blob that inflates to gigabytes (a zip bomb) and
// OOM the tab on scan or file import.
function capBytes(max: number): TransformStream<Uint8Array, Uint8Array> {
  let total = 0;
  return new TransformStream({
    transform(chunk, controller) {
      total += chunk.byteLength;
      if (total > max) controller.error(new Error("sync payload too large"));
      else controller.enqueue(chunk);
    },
  });
}

async function pipeBytes(
  bytes: Uint8Array,
  transform: { readable: ReadableStream; writable: WritableStream },
  maxBytes?: number,
): Promise<Uint8Array> {
  let stream = new Blob([bytes as BlobPart]).stream().pipeThrough(transform);
  if (maxBytes !== undefined) stream = stream.pipeThrough(capBytes(maxBytes));
  return new Uint8Array(await new Response(stream).arrayBuffer());
}

// No honest ledger moved through the compressed QR/file path comes near this.
const MAX_INFLATED_BYTES = 16 * 1024 * 1024;

export const deflate = (bytes: Uint8Array) => pipeBytes(bytes, new CompressionStream("deflate-raw"));
const inflate = (bytes: Uint8Array) =>
  pipeBytes(bytes, new DecompressionStream("deflate-raw"), MAX_INFLATED_BYTES);

export async function encryptMsg(groupKey: Uint8Array, msg: WireMsg): Promise<Envelope> {
  return encryptPayload(groupKey, utf8ToBytes(canonicalize(msg as never)));
}

export async function decryptMsg(
  groupKey: Uint8Array,
  envelope: Envelope,
  compressed = false,
): Promise<WireMsg> {
  let plain = await decryptPayload(groupKey, envelope);
  if (compressed) {
    if (!canCompress) throw new Error("this sync code needs a newer browser to read");
    plain = await inflate(plain);
  }
  const parsed = JSON.parse(decoder.decode(plain)) as WireMsg;
  if (parsed && parsed.t === "ops" && parsed.b && parsed.b.v === 1) return parsed;
  if (
    parsed &&
    parsed.t === "hello" &&
    isHex64(parsed.pub) &&
    typeof parsed.name === "string" &&
    parsed.name.length <= 64
  ) {
    return parsed;
  }
  if (
    parsed &&
    parsed.t === "req" &&
    Array.isArray(parsed.heads) &&
    parsed.heads.length <= 128 &&
    parsed.heads.every(isHex64)
  ) {
    return parsed;
  }
  throw new Error("unrecognized wire message");
}

// ---------------------------------------------------------------------------
// QR payload encodings.
//
// v1 (legacy): base64url of the envelope JSON - the ciphertext gets base64'd
// TWICE, a ~33% tax on already-encrypted bytes. Kept readable for old frames.
//
// v2 (PS2 frames): "<flag>:<nonce>:<ciphertext>", flag "z" when the plaintext
// was deflated before encryption, "r" when not. Same AES-GCM envelope, one
// base64 pass, roughly a third of the v1 size before compression even counts.
// ---------------------------------------------------------------------------

export function envelopeToQrPayload(envelope: Envelope): string {
  return toB64Url(utf8ToBytes(canonicalize(envelope as never)));
}

export function qrPayloadToEnvelope(payload: string): Envelope {
  const parsed = JSON.parse(decoder.decode(fromB64Url(payload))) as Envelope;
  if (!parsed || parsed.v !== 1 || typeof parsed.n !== "string" || typeof parsed.c !== "string") {
    throw new Error("not a psplit envelope");
  }
  return parsed;
}

export async function encryptMsgToQrPayload(groupKey: Uint8Array, msg: WireMsg): Promise<string> {
  const plain = utf8ToBytes(canonicalize(msg as never));
  const packed = canCompress ? await deflate(plain) : plain;
  const env = await encryptPayload(groupKey, packed);
  return `${canCompress ? "z" : "r"}:${env.n}:${env.c}`;
}

export function isV2QrPayload(payload: string): boolean {
  return payload.startsWith("z:") || payload.startsWith("r:");
}

export function parseQrPayload(payload: string): { envelope: Envelope; compressed: boolean } {
  if (isV2QrPayload(payload)) {
    const parts = payload.split(":");
    const [flag, n, c] = parts;
    if (parts.length !== 3 || n === undefined || n === "" || c === undefined || c === "") {
      throw new Error("not a psplit sync code");
    }
    return { envelope: { v: 1, n, c }, compressed: flag === "z" };
  }
  return { envelope: qrPayloadToEnvelope(payload), compressed: false };
}
